import React from 'react';
import Dialog from '@mui/material/Dialog';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { MDRT_COMMISSION, differenceOf, formatMoney, formatPeople } from './incomeModel';
import type { TeamInputs } from './incomeModel';

/**
 * Tap a bar in the 5-year chart and this opens that year as a picture: who is on
 * the team, where each slice of the income comes from, and how it compares.
 * Few numbers, big ones — it is meant to be talked through, not studied.
 */

type Row = Record<string, number>;
type Series = { key: string; label: string; color: string };

/** Most heads drawn before the rest collapse into a "+N" chip. */
const MAX_HEADS = 12;

const Person = ({ color, small }: { color: string; small?: boolean }) => (
  <svg className={`isim-person${small ? ' is-small' : ''}`} viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="8" r="4.2" fill={color} />
    <path d="M3.5 21c0-4.6 3.8-7.6 8.5-7.6s8.5 3 8.5 7.6z" fill={color} />
  </svg>
);

const Heads = ({ count, color, small }: { count: number; color: string; small?: boolean }) => {
  const whole = Math.max(0, Math.round(count));
  const shown = Math.min(whole, MAX_HEADS);
  return (
    <div className="isim-heads">
      {Array.from({ length: shown }, (_, i) => (
        <Person key={i} color={color} small={small} />
      ))}
      {whole > shown && <span className="isim-heads-more">+{whole - shown}</span>}
    </div>
  );
};

/** One slice of the year's income as a bar sized against the largest slice. */
const Slice = (props: { label: string; color: string; amount: number; max: number }) => {
  const { label, color, amount, max } = props;
  return (
    <div className="isim-slice">
      <div className="isim-slice-head">
        <span>
          <i style={{ background: color }} />
          {label}
        </span>
        <b>{formatMoney(amount)}</b>
      </div>
      <div className="isim-slice-track">
        <div style={{ width: `${max > 0 ? Math.max(3, (amount / max) * 100) : 0}%`, background: color }} />
      </div>
    </div>
  );
};

type Props = {
  year: number | null;
  mode: 'new' | 'team';
  rows: Row[];
  series: Series[];
  team: TeamInputs;
  onYear: (year: number | null) => void;
  /** Looks up an `isim-` message. */
  t: (id: string, values?: Record<string, string | number>) => string;
};

const YearDetailDialog = ({ year, mode, rows, series, team, onYear, t }: Props) => {
  const open = year !== null;
  // Keep the last year on screen while the dialog fades out.
  const [last, setLast] = React.useState(0);
  React.useEffect(() => {
    if (year !== null) setLast(year);
  }, [year]);
  const idx = Math.min(year !== null ? year : last, rows.length - 1);
  const row = rows[idx];
  if (!row) return null;
  const find = (key: string) => series.find(s => s.key === key);
  const color = (key: string) => (find(key) ? (find(key) as Series).color : '#64748b');
  const label = (key: string) => (find(key) ? (find(key) as Series).label : '');
  const close = () => onYear(null);
  const max = Math.max(...series.map(s => row[s.key] || 0));
  const gap = differenceOf(row.total, row.reference);
  const mdrtGap = MDRT_COMMISSION - row.total;
  const mdrtCell = (
    <div className={mdrtGap <= 0 ? 'is-good' : ''}>
      <span>{t('mdrt-threshold')}</span>
      <b>{mdrtGap <= 0 ? t('mdrt-reached') : t('mdrt-short', { amount: formatMoney(mdrtGap) })}</b>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="xs"
      // The simulator sits inside a dialog at z-index 1301; the pop-up has to float above it.
      sx={{ zIndex: 1400 }}
      slotProps={{ paper: { className: 'isim-pop' } }}
      data-testid="income-sim-year"
    >
      <div className="isim-pop-bar">
        <button type="button" aria-label={t('prev-year')} disabled={idx === 0} onClick={() => onYear(idx - 1)}>
          <ChevronLeftRoundedIcon />
        </button>
        <span className="isim-pop-year">{t('year', { n: idx + 1 })}</span>
        <button
          type="button"
          aria-label={t('next-year')}
          disabled={idx === rows.length - 1}
          onClick={() => onYear(idx + 1)}
        >
          <ChevronRightRoundedIcon />
        </button>
        <button type="button" className="isim-pop-close" aria-label={t('close')} onClick={close}>
          <CloseRoundedIcon />
        </button>
      </div>

      {mode === 'team' ? (
        <div className="isim-pop-body">
          <div className="isim-pop-hero">
            <span>{t('pop-team-hero')}</span>
            <b>{formatMoney((row.direct + row.second) / 12)}</b>
            <small>{t('pop-total-monthly', { amount: formatMoney(row.total / 12) })}</small>
          </div>

          <div className="isim-tree" aria-label={t('tree-aria')}>
            <div className="isim-tree-row">
              <Person color={color('own')} />
              <span className="isim-tree-tag">{t('tree-you')}</span>
            </div>
            <div className="isim-tree-link" />
            <div className="isim-tree-row">
              <Heads count={row.directMembers} color={color('direct')} />
              <span className="isim-tree-tag">{t('tree-direct', { n: formatPeople(row.directMembers) })}</span>
            </div>
            {team.secondGeneration && row.secondMembers > 0 && (
              <>
                <div className="isim-tree-link" />
                <div className="isim-tree-row">
                  <Heads count={row.secondMembers} color={color('second')} small />
                  <span className="isim-tree-tag">{t('tree-second', { n: formatPeople(row.secondMembers) })}</span>
                </div>
              </>
            )}
          </div>

          <div className="isim-slices">
            <Slice label={label('own')} color={color('own')} amount={row.own} max={max} />
            <Slice label={label('direct')} color={color('direct')} amount={row.direct} max={max} />
            {team.secondGeneration && (
              <Slice label={label('second')} color={color('second')} amount={row.second} max={max} />
            )}
          </div>

          <div className="isim-pop-foot">
            <div>
              <span>{t('row-more-solo')}</span>
              <b>
                {gap.amount} <em>{gap.percent}</em>
              </b>
            </div>
            {mdrtCell}
          </div>
        </div>
      ) : (
        <div className="isim-pop-body">
          <div className="isim-pop-hero">
            <span>{t('pop-new-hero')}</span>
            <b>{formatMoney(row.total / 12)}</b>
            <small>
              {t('salary-now', { amount: formatMoney(row.reference / 12) })}
              {row.reference > 0
                ? ` · ${t('times-job', { times: Math.round((row.total / row.reference) * 10) / 10 })}`
                : ''}
            </small>
          </div>
          <div className="isim-slices">
            <Slice label={label('firstYear')} color={color('firstYear')} amount={row.firstYear} max={max} />
            <Slice label={label('renewal')} color={color('renewal')} amount={row.renewal} max={max} />
          </div>
          <div className="isim-pop-foot">
            <div>
              <span>{t('pop-more-job')}</span>
              <b>{gap.amount}</b>
            </div>
            {mdrtCell}
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default YearDetailDialog;
